import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    Button,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress
} from "@mui/material";

import {
    collection,
    getDocs,
    query,
    where,
    doc,
    getDoc,
    orderBy
} from "firebase/firestore";

import { db, auth } from "../../firebase";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function Feesreport() {

    const [loading, setLoading] = useState(false);

    const [schoolId, setSchoolId] = useState("");

    const [courses, setCourses] = useState([]);

    const [filters, setFilters] = useState({
        fromDate: "",
        toDate: "",
        class: "",
        payMode: ""
    });

    const [reports, setReports] = useState([]);

    // ================= GET SCHOOL ID =================

    useEffect(() => {

        const fetchSchool = async () => {

            try {

                const uid = auth.currentUser?.uid;

                if (!uid) return;

                const userSnap = await getDoc(
                    doc(db, "users", uid)
                );

                if (userSnap.exists()) {

                    const data = userSnap.data();

                    setSchoolId(data.schoolId || "");

                }

            } catch (err) {

                console.log(err);

            }

        };

        fetchSchool();

    }, []);

    // ================= FETCH COURSES =================

    useEffect(() => {

        if (!schoolId) return;

        const fetchCourses = async () => {

            try {

                const q = query(
                    collection(db, "courses"),
                    where("schoolId", "==", schoolId),
                    orderBy("sequence", "asc")
                );

                const snap = await getDocs(q);

                setCourses(
                    snap.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }))
                );

            } catch (err) {

                console.log(err);

            }

        };

        fetchCourses();

    }, [schoolId]);

    // ================= HANDLE CHANGE =================

    const handleChange = (field, value) => {

        setFilters(prev => ({
            ...prev,
            [field]: value
        }));

    };

    // ================= FETCH REPORT =================

    const fetchReport = async () => {

        try {

            setLoading(true);

            // ================= GET FEE STRUCTURES =================

            const feeQuery = query(
                collection(db, "fee_structures")
            );

            const feeSnap = await getDocs(feeQuery);

            const feeStructures = feeSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // ================= ALL PAYMENTS =================

            let allPayments = [];

            feeStructures.forEach((fee) => {

                const payments = fee.paymentHistory || [];

                payments.forEach((payment) => {

                    allPayments.push({
                        admissionId: fee.admissionId || fee.id,
                        ...payment
                    });

                });

            });

            // ================= DATE FILTER =================

            if (filters.fromDate && filters.toDate) {

                const from = new Date(filters.fromDate);
                from.setHours(0, 0, 0, 0);

                const to = new Date(filters.toDate);
                to.setHours(23, 59, 59, 999);

                allPayments = allPayments.filter((item) => {

                    const paymentDate = new Date(item.date);

                    return paymentDate >= from &&
                        paymentDate <= to;

                });

            }

            // ================= PAYMENT MODE FILTER =================

            if (filters.payMode) {

                allPayments = allPayments.filter(
                    item => item.payMode === filters.payMode
                );

            }

            // ================= GET STUDENT DATA =================

            const reportPromises = allPayments.map(async (payment) => {

                try {

                    const admissionId =
                        payment.admissionId;

                    // ================= ADMISSION =================

                    const admissionSnap = await getDoc(
                        doc(db, "admission", admissionId)
                    );

                    let admission = {};

                    if (admissionSnap.exists()) {
                        admission = admissionSnap.data();
                    }

                    // ================= CLASS =================

                    let className = "";

                    const classObj = courses.find(
                        c => c.id === admission.class
                    );

                    if (classObj) {
                        className = classObj.name;
                    }

                    // ================= STUDENT =================

                    const studentId =
                        admission.studentId ||
                        admissionId;

                    const studentSnap = await getDoc(
                        doc(db, "students", studentId)
                    );

                    let student = {};

                    if (studentSnap.exists()) {
                        student = studentSnap.data();
                    }

                    // ================= CLASS FILTER =================

                    if (
                        filters.class &&
                        admission.class !== filters.class
                    ) {
                        return null;
                    }

                    return {

                        receiptNo:
                            payment.receiptNo || "",

                        paymentDate:
                            payment.date || "",

                        payMode:
                            payment.payMode || "",

                        paidAmount:
                            payment.paidAmount || 0,

                        rollno:
                            student.rollno || "",

                        admno:
                            student.admno || "",

                        studentName:
                            `${student.firstname || ""} ${student.fathername || ""} ${student.surname || ""}`,

                        fathername:
                            student.fathername || "",

                        mobile:
                            student.father_mobile1 ||
                            student.mobile ||
                            "",

                        gender:
                            student.gender || "",

                        className:
                            className || "",

                        installment:
                            parseInt(payment.installmentIndex) + 1 || "",

                    };

                } catch (err) {

                    console.log(err);

                    return null;

                }

            });

            const finalData = await Promise.all(
                reportPromises
            );

            const filtered =
                finalData.filter(Boolean);

            // ================= SORT =================

            filtered.sort((a, b) =>
                new Date(b.paymentDate) -
                new Date(a.paymentDate)
            );

            setReports(filtered);

            setLoading(false);

        } catch (err) {

            console.log(err);

            setLoading(false);

        }

    };

    // ================= PDF =================

    const downloadPDF = () => {

        const pdf = new jsPDF("landscape");

        pdf.setFontSize(16);

        pdf.text("Fees Collection Report", 14, 15);

        autoTable(pdf, {

            startY: 25,

            head: [[
                "Sr No",
                "Receipt No",
                "Date",
                "Roll No",
                "Adm No",
                "Student Name",
                "Class",
                "Mobile",
                "Mode",
                "Installment",
                "Amount"
            ]],

            body: reports.map((row, index) => ([

                index + 1,

                row.receiptNo,

                row.paymentDate,

                row.rollno,

                row.admno,

                row.studentName,

                row.className,

                row.mobile,

                row.payMode,

                row.installment,

                row.paidAmount

            ]))

        });

        pdf.save("fees-report.pdf");

    };

    // ================= TOTAL =================

    const totalAmount = reports.reduce(
        (sum, item) =>
            sum + Number(item.paidAmount || 0),
        0
    );

    return (

        <Box p={1}>

            <Paper sx={{ p: 3 }}>

                {/* HEADER */}

                <Box
                    sx={{
                        background: "#0e6363",
                        color: "#fff",
                        p: 1.5,
                        mb: 3
                    }}
                >
                    <Typography
                        fontWeight="bold"
                        fontSize={18}
                    >
                        Fees Collection Report
                    </Typography>
                </Box>

                {/* FILTERS */}

                <Grid className="row" spacing={2}>

                    {/* FROM DATE */}

                    <Grid className="col-sm-2 mb-3" item xs={12} sm={2}>

                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            label="From Date"
                            InputLabelProps={{
                                shrink: true
                            }}
                            value={filters.fromDate}
                            onChange={(e) =>
                                handleChange(
                                    "fromDate",
                                    e.target.value
                                )
                            }
                        />

                    </Grid>

                    {/* TO DATE */}

                    <Grid className="col-sm-2 mb-3" item xs={12} sm={2}>

                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            label="To Date"
                            InputLabelProps={{
                                shrink: true
                            }}
                            value={filters.toDate}
                            onChange={(e) =>
                                handleChange(
                                    "toDate",
                                    e.target.value
                                )
                            }
                        />

                    </Grid>

                    {/* CLASS */}

                    <Grid className="col-sm-2 mb-3" item xs={12} sm={2}>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Class"
                            value={filters.class}
                            onChange={(e) =>
                                handleChange(
                                    "class",
                                    e.target.value
                                )
                            }
                        >

                            <MenuItem value="">
                                All
                            </MenuItem>

                            {courses.map((item) => (

                                <MenuItem
                                    key={item.id}
                                    value={item.id}
                                >
                                    {item.name}
                                </MenuItem>

                            ))}

                        </TextField>

                    </Grid>

                    {/* PAY MODE */}

                    <Grid className="col-sm-2 mb-3" item xs={12} sm={2}>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Pay Mode"
                            value={filters.payMode}
                            onChange={(e) =>
                                handleChange(
                                    "payMode",
                                    e.target.value
                                )
                            }
                        >

                            <MenuItem value="">
                                All
                            </MenuItem>

                            <MenuItem value="Cash">
                                Cash
                            </MenuItem>

                            <MenuItem value="Online">
                                Online / UPI
                            </MenuItem>

                            <MenuItem value="Cheque">
                                Cheque
                            </MenuItem>

                            <MenuItem value="Transfer">
                                Bank Transfer
                            </MenuItem>

                        </TextField>

                    </Grid>

                    {/* BUTTONS */}

                    <Grid className="col-sm-2 mb-3" item xs={12} sm={2}>

                        <Box display="flex" gap={1}>

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={fetchReport}
                            >
                                Preview
                            </Button>

                            <Button
                                fullWidth
                                color="success"
                                variant="contained"
                                onClick={downloadPDF}
                                disabled={
                                    reports.length === 0
                                }
                            >
                                PDF
                            </Button>

                        </Box>

                    </Grid>

                    {/* TOTAL */}

                    <Grid className="col-sm-2 mb-3" item xs={12} sm={2}>

                        <Paper
                            sx={{
                                p: 1,
                                textAlign: "center",
                                background:
                                    "#e8f5e9"
                            }}
                        >

                            <Typography
                                fontSize={13}
                            >
                                Total Collection
                            </Typography>

                            <Typography
                                fontWeight="bold"
                                color="green"
                            >
                                ₹{totalAmount}
                            </Typography>

                        </Paper>

                    </Grid>

                </Grid>

                {/* TABLE */}

                <Box mt={4}>

                    {loading ? (

                        <Box textAlign="center">
                            <CircularProgress />
                        </Box>

                    ) : (

                        <TableContainer
                            component={Paper}
                        >

                            <Table size="small">

                                <TableHead>

                                    <TableRow>

                                        <TableCell>
                                            Sr No
                                        </TableCell>

                                        <TableCell>
                                            Receipt No
                                        </TableCell>

                                        <TableCell>
                                            Date
                                        </TableCell>

                                        <TableCell>
                                            Roll No
                                        </TableCell>

                                        <TableCell>
                                            Adm No
                                        </TableCell>

                                        <TableCell>
                                            Student Name
                                        </TableCell>

                                        <TableCell>
                                            Class
                                        </TableCell>

                                        <TableCell>
                                            Mobile
                                        </TableCell>

                                        <TableCell>
                                            Pay Mode
                                        </TableCell>

                                        <TableCell>
                                            Installment
                                        </TableCell>

                                        <TableCell>
                                            Amount
                                        </TableCell>

                                    </TableRow>

                                </TableHead>

                                <TableBody>

                                    {reports.length > 0 ? (

                                        reports.map(
                                            (
                                                row,
                                                index
                                            ) => (

                                                <TableRow
                                                    key={
                                                        index
                                                    }
                                                >

                                                    <TableCell>
                                                        {index + 1}
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.receiptNo
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.paymentDate
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.rollno
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.admno
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.studentName
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.className
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.mobile
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.payMode
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            row.installment
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        ₹
                                                        {
                                                            row.paidAmount
                                                        }
                                                    </TableCell>

                                                </TableRow>

                                            )
                                        )

                                    ) : (

                                        <TableRow>

                                            <TableCell
                                                colSpan={11}
                                                align="center"
                                            >
                                                No Data Found
                                            </TableCell>

                                        </TableRow>

                                    )}

                                </TableBody>

                            </Table>

                        </TableContainer>

                    )}

                </Box>

            </Paper>

        </Box>

    );
}

export default Feesreport;